import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Tags, Trash2 } from 'lucide-react'
import { Fragment, useState, type ReactNode } from 'react'

import type { PriceResponderSaveBody } from '@aeris/contracts'
import {
  Badge,
  Button,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  Section,
  Select,
  Spinner,
  SwitchField,
  TagInput,
  Textarea,
  type StatusTone,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import {
  ProductPicker,
  type PickedProduct,
} from '../components/product-picker.tsx'
import {
  ApiError,
  addResponderExclusion,
  fetchBranches,
  fetchPriceResponders,
  fetchResponderExclusions,
  fetchResponderLog,
  previewPriceResponder,
  removeResponderExclusion,
  savePriceResponder,
  type PriceResponderPreviewType,
  type PriceResponderType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { shortTimeOf } from '../lib/inbox.ts'

/**
 * Atendimento → Configuração → Robô de preço (F193).
 *
 * Um número da loja responde sozinho "tela a03" com as telas do A03 que têm
 * estoque na filial escolhida, e o preço — sem IA, por regra. A tela existe
 * para três perguntas:
 *
 * 1. **O que ele vai responder?** A caixa de teste roda o MESMO código do
 *    worker com o que está na tela, antes de salvar, e mostra também o que
 *    ficou de fora e por quê (intruso, sem estoque, sem preço, fora do robô).
 * 2. **Como deixar uma peça de fora?** "Fora do robô".
 * 3. **O que ele respondeu?** O histórico, com as perguntas que ficaram sem
 *    resposta — o silêncio também tem motivo.
 */

type Draft = PriceResponderSaveBody

const toDraft = (responder: PriceResponderType): Draft => ({
  enabled: responder.enabled,
  branchId: responder.branchId,
  minDelaySeconds: responder.minDelaySeconds,
  maxDelaySeconds: responder.maxDelaySeconds,
  maxItems: responder.maxItems,
  repeatWindowSeconds: responder.repeatWindowSeconds,
  maxRepliesPerHour: responder.maxRepliesPerHour,
  stopWords: responder.stopWords,
  intruders: responder.intruders,
  qualityLabels: responder.qualityLabels,
  brandTokens: responder.brandTokens,
  sections: responder.sections,
  version: responder.version,
})

const REJECTION_LABELS: Record<string, string> = {
  model: 'outro modelo',
  excluded: 'fora do robô',
  no_stock: 'sem estoque na filial',
  no_price: 'sem preço na tabela padrão',
}

/** Por que o robô ficou quieto numa pergunta que cita peça (F244). */
const SILENCE_EXPLANATIONS: Record<string, string> = {
  price:
    'O robô fica quieto: a mensagem já tem preço. O cliente está pedindo, e a vendedora confirma.',
  after_sale:
    'O robô fica quieto: parece conversa de pós-venda (troca, defeito, entrega). A vendedora responde.',
  vague:
    'O robô fica quieto: a pergunta não diz o modelo, como "tela do iPhone". A vendedora pergunta qual.',
}

const SILENCE_LABELS: Record<string, string> = {
  price: 'Quieto: já tinha preço',
  after_sale: 'Quieto: pós-venda',
  vague: 'Quieto: pergunta vaga',
}

const OUTCOME_LABELS: Record<string, { label: string; tone: StatusTone }> = {
  replied: { label: 'Respondeu', tone: 'success' },
  no_match: { label: 'Não achou peça', tone: 'warning' },
  no_trigger: { label: 'Não era pergunta de preço', tone: 'neutral' },
  repeat: { label: 'Pergunta repetida', tone: 'neutral' },
  rate_capped: { label: 'Teto por hora', tone: 'danger' },
  own_number: { label: 'Número da própria loja', tone: 'neutral' },
  looks_like_bot: { label: 'Parecia outro robô', tone: 'neutral' },
  not_text: { label: 'Não era texto', tone: 'neutral' },
  no_branch: { label: 'Sem filial', tone: 'danger' },
  disabled: { label: 'Robô desligado', tone: 'neutral' },
}

/** O Markdown da outbox como o WhatsApp mostra: `**x**` em negrito. */
const WhatsappText = ({ text }: { text: string }): ReactNode => (
  <p className="whitespace-pre-wrap rounded-(--radius-md) bg-(--surface-sunken) p-3 text-(length:--text-sm) text-(--text-primary)">
    {text
      .split(/(\*\*[^*]+\*\*)/g)
      .map((part, index) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={index}>{part.slice(2, -2)}</strong>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        )
      )}
  </p>
)

const numberField = (value: string): number =>
  value.trim() === '' ? 0 : Math.trunc(Number(value))

const ResponderEditor = ({
  responder,
  canManage,
}: {
  responder: PriceResponderType
  canManage: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft>(() => toDraft(responder))
  const [message, setMessage] = useState('tela a03')
  const [preview, setPreview] = useState<PriceResponderPreviewType | null>(null)
  const [notice, setNotice] = useState<{
    tone: 'success' | 'danger'
    text: string
  } | null>(null)
  const [picked, setPicked] = useState<PickedProduct | null>(null)

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    setDraft((previous) => ({ ...previous, [key]: value }))
  }

  const branches = useQuery({
    queryKey: ['organization-branches'],
    queryFn: async ({ signal }) => await fetchBranches(undefined, signal),
  })

  const exclusions = useQuery({
    queryKey: ['price-responder-exclusions', responder.accountId],
    queryFn: async ({ signal }) =>
      await fetchResponderExclusions(responder.accountId, signal),
    enabled: responder.configured,
  })

  const log = useQuery({
    queryKey: ['price-responder-log', responder.accountId],
    queryFn: async ({ signal }) =>
      await fetchResponderLog(responder.accountId, null, signal),
    enabled: responder.configured,
  })

  const failure = (cause: unknown, fallback: string): void => {
    setNotice({
      tone: 'danger',
      text: cause instanceof ApiError ? cause.message : fallback,
    })
  }

  const save = useMutation({
    mutationFn: async () =>
      await savePriceResponder(responder.accountId, draft),
    onSuccess: async (saved) => {
      setDraft(toDraft(saved))
      setNotice({
        tone: 'success',
        text: saved.enabled
          ? 'Salvo. O robô já responde por este número.'
          : 'Salvo. O robô está desligado neste número.',
      })
      await queryClient.invalidateQueries({ queryKey: ['price-responders'] })
    },
    onError: (cause: unknown) => {
      failure(cause, 'Não foi possível salvar o robô.')
    },
  })

  const test = useMutation({
    mutationFn: async () =>
      await previewPriceResponder(responder.accountId, message, draft),
    onSuccess: setPreview,
    onError: (cause: unknown) => {
      failure(cause, 'Não foi possível testar agora.')
    },
  })

  const exclusionChange = async (
    action: () => Promise<unknown>
  ): Promise<void> => {
    try {
      await action()
      await queryClient.invalidateQueries({
        queryKey: ['price-responder-exclusions', responder.accountId],
      })
    } catch (cause) {
      failure(cause, 'Não foi possível mudar as peças fora do robô.')
    }
  }

  const sections = draft.sections
  const updateSection = (
    index: number,
    patch: Partial<Draft['sections'][number]>
  ): void => {
    set(
      'sections',
      sections.map((section, position) =>
        position === index ? { ...section, ...patch } : section
      )
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {notice === null ? null : (
        <InlineAlert tone={notice.tone}>{notice.text}</InlineAlert>
      )}

      <Section
        title="Ligar"
        description="O robô responde neste número as perguntas que citam uma seção e um modelo. O que não for pergunta de preço fica para um atendente."
      >
        <div className="flex flex-col gap-4">
          <SwitchField
            label="Responder a tabela de preço por este número"
            description="Neste número o robô de IA não é chamado."
            checked={draft.enabled}
            disabled={!canManage}
            onCheckedChange={(checked) => {
              set('enabled', checked)
            }}
          />
          <Field
            label="Filial do estoque"
            hint="Só aparecem as peças com estoque disponível nesta filial."
            required={draft.enabled}
          >
            {(field) => (
              <Select
                {...field}
                value={draft.branchId ?? ''}
                disabled={!canManage}
                onChange={(event) => {
                  set(
                    'branchId',
                    event.target.value === '' ? null : event.target.value
                  )
                }}
              >
                <option value="">Escolha a filial</option>
                {(branches.data?.branches ?? []).map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Espera mínima (s)" hint="Para não parecer robô.">
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={0}
                  max={120}
                  value={draft.minDelaySeconds}
                  disabled={!canManage}
                  onChange={(event) => {
                    set('minDelaySeconds', numberField(event.target.value))
                  }}
                />
              )}
            </Field>
            <Field label="Espera máxima (s)">
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={0}
                  max={120}
                  value={draft.maxDelaySeconds}
                  disabled={!canManage}
                  onChange={(event) => {
                    set('maxDelaySeconds', numberField(event.target.value))
                  }}
                />
              )}
            </Field>
            <Field label="Máximo de peças por seção">
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  max={50}
                  value={draft.maxItems}
                  disabled={!canManage}
                  onChange={(event) => {
                    set('maxItems', numberField(event.target.value))
                  }}
                />
              )}
            </Field>
          </div>
        </div>
      </Section>

      <Section
        title="Seções"
        description="Cada seção é um cabeçalho na resposta. A palavra na pergunta chama a seção; o prefixo diz quais peças do cadastro são dela, e sai do nome na resposta."
        action={
          canManage ? (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                set('sections', [
                  ...sections,
                  {
                    title: '',
                    emoji: '📦',
                    triggers: [],
                    namePrefix: '',
                    categoryId: null,
                  },
                ])
              }}
            >
              Nova seção
            </Button>
          ) : null
        }
      >
        <ul className="flex flex-col divide-y divide-(--border-subtle)">
          {sections.map((section, index) => (
            <li
              key={index}
              className="grid grid-cols-1 gap-3 py-3 sm:grid-cols-[4rem_1fr_8rem_auto] sm:items-end"
            >
              <Field label="Emoji">
                {(field) => (
                  <Input
                    {...field}
                    value={section.emoji}
                    disabled={!canManage}
                    onChange={(event) => {
                      updateSection(index, { emoji: event.target.value })
                    }}
                  />
                )}
              </Field>
              <Field label="Título">
                {(field) => (
                  <Input
                    {...field}
                    value={section.title}
                    disabled={!canManage}
                    onChange={(event) => {
                      updateSection(index, { title: event.target.value })
                    }}
                  />
                )}
              </Field>
              <Field label="Prefixo no cadastro">
                {(field) => (
                  <Input
                    {...field}
                    value={section.namePrefix ?? ''}
                    placeholder="f."
                    disabled={!canManage}
                    onChange={(event) => {
                      updateSection(index, {
                        namePrefix:
                          event.target.value === '' ? null : event.target.value,
                      })
                    }}
                  />
                )}
              </Field>
              {canManage ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remover a seção ${section.title}`}
                  leadingIcon={
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                  }
                  onClick={() => {
                    set(
                      'sections',
                      sections.filter((_, position) => position !== index)
                    )
                  }}
                >
                  Remover
                </Button>
              ) : null}
              <Field
                label="Palavras que chamam a seção"
                className="sm:col-span-4"
              >
                {(field) => (
                  <TagInput
                    {...field}
                    value={section.triggers}
                    disabled={!canManage}
                    onValueChange={(triggers) => {
                      updateSection(index, { triggers })
                    }}
                    placeholder="tela, display, frontal…"
                  />
                )}
              </Field>
            </li>
          ))}
        </ul>
      </Section>

      <details className="rounded-(--radius-lg) border border-(--border-default) bg-(--surface) px-4 py-3">
        <summary className="cursor-pointer text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
          Avançado — palavras, intrusos e tetos
        </summary>
        <div className="mt-4 flex flex-col gap-4">
          <Field
            label="Intrusos"
            hint={
              'O que vem DEPOIS do modelo e muda a peça: "a03" não traz "A03 CORE" porque "core" é intruso.'
            }
          >
            {(field) => (
              <TagInput
                {...field}
                value={draft.intruders}
                disabled={!canManage}
                onValueChange={(value) => {
                  set('intruders', value)
                }}
              />
            )}
          </Field>
          <Field
            label="Palavras ignoradas na pergunta"
            hint={'"oi", "tem", "valor" — não fazem parte do modelo.'}
          >
            {(field) => (
              <TagInput
                {...field}
                value={draft.stopWords}
                disabled={!canManage}
                onValueChange={(value) => {
                  set('stopWords', value)
                }}
              />
            )}
          </Field>
          <Field
            label="Marcas de qualidade no nome"
            hint={
              '"Maxter Pro" é marca de tela — o "pro" dela não é o do iPhone.'
            }
          >
            {(field) => (
              <TagInput
                {...field}
                value={draft.qualityLabels}
                disabled={!canManage}
                onValueChange={(value) => {
                  set('qualityLabels', value)
                }}
              />
            )}
          </Field>
          <Field
            label="Marcas que o cadastro não escreve"
            hint={
              '"tela samsung a03" procura "a03" quando nenhum nome tem "samsung".'
            }
          >
            {(field) => (
              <TagInput
                {...field}
                value={draft.brandTokens}
                disabled={!canManage}
                onValueChange={(value) => {
                  set('brandTokens', value)
                }}
              />
            )}
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Não repetir a mesma pergunta por (s)"
              hint="Na mesma conversa. Zero responde sempre."
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={0}
                  value={draft.repeatWindowSeconds}
                  disabled={!canManage}
                  onChange={(event) => {
                    set('repeatWindowSeconds', numberField(event.target.value))
                  }}
                />
              )}
            </Field>
            <Field
              label="Máximo de respostas por hora"
              hint="Por número — protege o WhatsApp da loja de bloqueio."
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  value={draft.maxRepliesPerHour}
                  disabled={!canManage}
                  onChange={(event) => {
                    set('maxRepliesPerHour', numberField(event.target.value))
                  }}
                />
              )}
            </Field>
          </div>
        </div>
      </details>

      {canManage ? (
        <div className="flex justify-end">
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => {
              setNotice(null)
              save.mutate()
            }}
          >
            Salvar
          </Button>
        </div>
      ) : null}

      <Section
        title="Testar"
        description="Escreva como o cliente escreveria. A resposta usa o que está na tela, mesmo antes de salvar, e nada é enviado."
      >
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault()
            setNotice(null)
            test.mutate()
          }}
        >
          <Field
            label="Mensagem do cliente"
            hint="Várias peças, uma por linha, também valem."
            className="flex-1"
          >
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value)
                }}
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="secondary"
            loading={test.isPending}
            disabled={message.trim() === ''}
          >
            Testar
          </Button>
        </form>

        {preview === null ? null : (
          <div className="mt-4 flex flex-col gap-3" aria-live="polite">
            {preview.outcome === 'no_trigger' ? (
              <InlineAlert tone="info">
                Sem resposta: a mensagem não cita uma seção e um modelo.
              </InlineAlert>
            ) : preview.outcome === 'no_branch' ? (
              <InlineAlert tone="warning">
                Escolha a filial do estoque para o robô poder responder.
              </InlineAlert>
            ) : preview.silence !== null ? (
              <InlineAlert tone="info">
                {SILENCE_EXPLANATIONS[preview.silence] ?? preview.silence}
              </InlineAlert>
            ) : preview.body === null ? (
              <InlineAlert tone="warning">
                Sem resposta: nenhuma peça de {preview.model.join(' ')} com
                estoque e preço. O cliente fica sem mensagem e a conversa espera
                um atendente.
              </InlineAlert>
            ) : (
              <WhatsappText text={preview.body} />
            )}
            {preview.ignored.length > 0 ? (
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                Palavras que o robô deixou de lado: {preview.ignored.join(', ')}
              </p>
            ) : null}
            {preview.stockIgnored ? (
              <InlineAlert tone="info">
                A empresa desta filial não controla estoque: o robô mostra as
                peças com preço, tenham saldo ou não.
              </InlineAlert>
            ) : null}
            {preview.sections.some((section) => section.rejected.length > 0) ? (
              <details>
                <summary className="cursor-pointer text-(length:--text-sm) text-(--text-secondary)">
                  O que ficou de fora
                </summary>
                <ul className="mt-2 flex flex-col gap-1 text-(length:--text-sm)">
                  {preview.sections.flatMap((section) =>
                    section.rejected.map((entry) => (
                      <li
                        key={`${section.title}-${entry.productId}`}
                        className="flex flex-wrap justify-between gap-2"
                      >
                        <span className="text-(--text-primary)">
                          {entry.name}
                        </span>
                        <span className="text-(--text-muted)">
                          {REJECTION_LABELS[entry.reason] ?? entry.reason}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </details>
            ) : null}
          </div>
        )}
      </Section>

      <Section
        title="Fora do robô"
        description="Peças que têm estoque e preço, mas não devem ser cotadas pelo WhatsApp."
      >
        {!responder.configured ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Salve o robô deste número para tirar peças dele.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {canManage ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <Field label="Peça" className="flex-1">
                  {(field) => (
                    <ProductPicker
                      {...field}
                      value={picked}
                      onChange={setPicked}
                    />
                  )}
                </Field>
                <Button
                  variant="secondary"
                  disabled={picked?.productId === undefined}
                  onClick={() => {
                    const productId = picked?.productId
                    if (productId === undefined) return
                    setPicked(null)
                    void exclusionChange(
                      async () =>
                        await addResponderExclusion(
                          responder.accountId,
                          productId
                        )
                    )
                  }}
                >
                  Tirar do robô
                </Button>
              </div>
            ) : null}
            {(exclusions.data ?? []).length === 0 ? (
              <p className="text-(length:--text-sm) text-(--text-muted)">
                Nenhuma peça fora do robô.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-(--border-subtle)">
                {(exclusions.data ?? []).map((entry) => (
                  <li
                    key={entry.productId}
                    className="flex items-center justify-between gap-2 py-2 text-(length:--text-sm)"
                  >
                    <span className="text-(--text-primary)">
                      {entry.productName}
                    </span>
                    {canManage ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Devolver ${entry.productName} ao robô`}
                        onClick={() => {
                          void exclusionChange(
                            async () =>
                              await removeResponderExclusion(
                                responder.accountId,
                                entry.productId
                              )
                          )
                        }}
                      >
                        Devolver
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Section>

      <Section
        title="Histórico"
        description="As últimas mensagens que chegaram a este número e o que o robô fez com cada uma."
      >
        {!responder.configured ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nada ainda.
          </p>
        ) : log.isPending ? (
          <Spinner />
        ) : (log.data?.entries ?? []).length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nenhuma mensagem chegou ao robô ainda.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-(--border-subtle)">
            {(log.data?.entries ?? []).map((entry) => {
              const silence =
                entry.silence === null
                  ? undefined
                  : SILENCE_LABELS[entry.silence]
              const outcome =
                silence === undefined
                  ? (OUTCOME_LABELS[entry.outcome] ?? {
                      label: entry.outcome,
                      tone: 'neutral' as const,
                    })
                  : { label: silence, tone: 'neutral' as const }
              return (
                <li key={entry.id} className="flex flex-col gap-1 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-(length:--text-sm) text-(--text-primary)">
                      “{entry.query}”
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge tone={outcome.tone}>
                        {entry.outcome === 'replied'
                          ? `${outcome.label} · ${String(entry.itemCount)} peças`
                          : outcome.label}
                      </Badge>
                      <span className="text-(length:--text-xs) text-(--text-muted)">
                        {shortTimeOf(entry.createdAt)}
                      </span>
                    </span>
                  </div>
                  {entry.replyBody === null ? null : (
                    <details>
                      <summary className="cursor-pointer text-(length:--text-xs) text-(--text-secondary)">
                        Ver a resposta
                      </summary>
                      <WhatsappText text={entry.replyBody} />
                    </details>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </Section>
    </div>
  )
}

export const AtendimentoRoboPrecoPage = (): ReactNode => {
  const { allows } = useAuth()
  const canManage = allows('communication.bot.manage')
  const [chosen, setChosen] = useState<string | null>(null)

  const responders = useQuery({
    queryKey: ['price-responders'],
    queryFn: async ({ signal }) => await fetchPriceResponders(signal),
  })

  const rows = responders.data ?? []
  const selected =
    rows.find((row) => row.accountId === chosen) ??
    rows.find((row) => row.enabled) ??
    rows[0] ??
    null

  return (
    <>
      <TabHeader
        title="Robô de preço"
        description="O número que responde sozinho a tabela de preço, com o estoque da filial."
      />

      {responders.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : responders.isError ? (
        <EmptyState
          icon={<Tags aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar o robô de preço"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : selected === null ? (
        <EmptyState
          icon={<Tags aria-hidden="true" className="h-8 w-8" />}
          title="Nenhum número de WhatsApp"
          description="Conecte um número em Canais para ligar o robô de preço nele."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {rows.length > 1 ? (
            <Field label="Número">
              {(field) => (
                <Select
                  {...field}
                  value={selected.accountId}
                  className="sm:w-72"
                  onChange={(event) => {
                    setChosen(event.target.value)
                  }}
                >
                  {rows.map((row) => (
                    <option key={row.accountId} value={row.accountId}>
                      {row.accountDisplayName}
                      {row.enabled ? ' — robô ligado' : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
          <ResponderEditor
            key={`${selected.accountId}-${String(selected.version)}`}
            responder={selected}
            canManage={canManage}
          />
        </div>
      )}
    </>
  )
}
